/**
 * Responsibility: turn the plain vertex arrays produced by `world/tile.js` into GPU
 * geometry.
 *
 * This is the boundary between pure world logic and Three.js. Everything upstream deals
 * in numbers; everything from here on deals in scene objects.
 *
 * ## Colour space
 *
 * `world/palette.js` returns sRGB components, matching what the hex values in
 * `config.js` look like in a colour picker. Three.js expects vertex colour attributes in
 * its linear working space, so they are converted here. Uploading sRGB values as if they
 * were linear would make everything render noticeably brighter and more washed out than
 * the configured palette, which would make the look impossible to tune predictably.
 *
 * Conversion uses Three.js's own `setRGB` with an explicit source colour space rather
 * than a hand-written transfer function, so there is no duplicated colour maths.
 */

import * as THREE from 'three';

import { OCCLUSION } from '../config.js';

/** Floats per vertex, for both position and colour attributes. */
const COMPONENTS_PER_VERTEX = 3;

/**
 * Create the material shared by every terrain tile.
 *
 * One material is deliberately shared across all tiles: they are visually identical, and
 * a material per tile would multiply shader program switches for no benefit.
 *
 * Note the line width limitation documented in the steering file — `linewidth` is ignored
 * on every major browser, so lines are always one pixel. The glow comes from bloom, not
 * from thickness.
 *
 * @returns {THREE.LineBasicMaterial}
 */
export function createTileMaterial() {
  return new THREE.LineBasicMaterial({
    // Per-vertex colour is what produces the elevation gradient.
    vertexColors: true,
    // Must respect fog, otherwise distant terrain never fades and the loading
    // boundary becomes visible.
    fog: true,
    transparent: false,
  });
}

/**
 * Convert an sRGB colour array into Three.js's linear working space, in place.
 *
 * @param {Float32Array} colors - Interleaved r, g, b components in 0..1 sRGB.
 * @returns {Float32Array} The same array, converted.
 */
function convertColorsToWorkingSpace(colors) {
  // One reusable instance; allocating per vertex would create thousands of objects per tile.
  const scratch = new THREE.Color();

  for (let i = 0; i < colors.length; i += COMPONENTS_PER_VERTEX) {
    scratch.setRGB(colors[i], colors[i + 1], colors[i + 2], THREE.SRGBColorSpace);
    colors[i] = scratch.r;
    colors[i + 1] = scratch.g;
    colors[i + 2] = scratch.b;
  }

  return colors;
}

/**
 * Build a renderable line mesh for one tile.
 *
 * @param {{positions: Float32Array, colors: Float32Array}} tileData - Output of `buildTile`.
 * @param {THREE.LineBasicMaterial} material - Shared tile material.
 * @returns {THREE.LineSegments}
 */
export function createTileMesh(tileData, material) {
  const geometry = new THREE.BufferGeometry();

  geometry.setAttribute(
    'position',
    new THREE.BufferAttribute(tileData.positions, COMPONENTS_PER_VERTEX),
  );
  geometry.setAttribute(
    'color',
    new THREE.BufferAttribute(convertColorsToWorkingSpace(tileData.colors), COMPONENTS_PER_VERTEX),
  );

  /*
    Compute the bounding sphere explicitly so frustum culling works. Without it Three.js
    derives one lazily, but tile positions are in absolute world coordinates far from the
    origin, and an absent or stale bound is a common cause of tiles vanishing unexpectedly.
  */
  geometry.computeBoundingSphere();

  const mesh = new THREE.LineSegments(geometry, material);
  mesh.frustumCulled = true;

  /*
    Vertices are stored relative to the tile's own corner, so the origin lives here instead. That is
    what keeps the numbers the vertex shader sees small however far the player has flown — see the
    precision note in `world/tile.js`.

    Three.js composes matrices in float64 on the CPU and uploads only the camera-relative result, so
    a large value here costs nothing in precision.
  */
  mesh.position.set(tileData.originX, 0, tileData.originZ);
  freezeTransform(mesh);

  return mesh;
}

/**
 * Stop Three.js recomputing a transform that will never change again.
 *
 * A tile is placed once and stays there for its whole life — nothing in the world moves. By default
 * Three.js composes the local matrix from position, quaternion and scale, then multiplies it into a world
 * matrix, **for every object on every frame**. With around two hundred meshes resident that is four hundred
 * matrix operations a frame spent confirming that nothing moved.
 *
 * The one rule that comes with it: anything that changes the transform afterwards has to call
 * `updateMatrix` itself. Nothing does, and a floating origin would shift the parent group rather than
 * individual tiles.
 *
 * @param {THREE.Object3D} object
 */
function freezeTransform(object) {
  object.matrixAutoUpdate = false;
  object.updateMatrix();
}

/**
 * Create the material shared by every depth mask.
 *
 * Writes depth and nothing else. `colorWrite: false` is what makes it invisible without having
 * to match the background colour, so it stays correct if the background is ever lifted off pure
 * black. Lines drawn afterwards that sit behind a masked surface fail the depth test and are
 * never rasterised — which is the point: they cost nothing and, crucially, contribute nothing
 * to bloom.
 *
 * Face culling is off because the camera can legitimately end up below a ridge line during a
 * steep climb, and a one-sided mask would stop occluding from there.
 *
 * @returns {THREE.MeshBasicMaterial}
 */
export function createMaskMaterial() {
  return new THREE.MeshBasicMaterial({
    colorWrite: false,
    depthWrite: true,
    depthTest: true,
    side: THREE.DoubleSide,
    fog: false,

    /*
      Terrain lines lie exactly on the surface that masks them. Without a bias the two compete
      for the same depth value and lines flicker in and out at distance; the bias pushes the
      mask away from the camera so the lines reliably win.
    */
    polygonOffset: true,
    polygonOffsetFactor: OCCLUSION.DEPTH_BIAS_FACTOR,
    polygonOffsetUnits: OCCLUSION.DEPTH_BIAS_UNITS,
  });
}

/**
 * Build the depth mask mesh for one tile.
 *
 * @param {{positions: Float32Array, indices: Uint32Array}} mask - From `tileContent`.
 * @param {THREE.MeshBasicMaterial} material - Shared mask material.
 * @param {number} originX - Tile origin, since mask vertices are tile-relative like the lines.
 * @param {number} originZ
 * @returns {THREE.Mesh|null} Null when the tile needs no mask.
 */
export function createMaskMesh(mask, material, originX = 0, originZ = 0) {
  if (!mask || !mask.positions || !mask.indices || mask.indices.length === 0) {
    return null;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    'position',
    new THREE.BufferAttribute(mask.positions, COMPONENTS_PER_VERTEX),
  );
  geometry.setIndex(new THREE.BufferAttribute(mask.indices, 1));
  geometry.computeBoundingSphere();

  const mesh = new THREE.Mesh(geometry, material);
  mesh.frustumCulled = true;

  // Must match the line mesh exactly, or the mask would occlude the wrong part of the world.
  mesh.position.set(originX, 0, originZ);
  freezeTransform(mesh);

  /*
    Must be drawn before the lines it occludes. Three.js sorts opaque objects by material and
    distance, which gives no guarantee across two different materials, so the order is stated
    explicitly rather than hoped for.
  */
  mesh.renderOrder = -1;

  return mesh;
}

/**
 * Release a tile mesh's GPU memory.
 *
 * The shared material is deliberately *not* disposed here — it belongs to whoever created
 * it and is still in use by every other tile. Geometry is per-tile and must be released,
 * or flying for a while would leak steadily until the tab died.
 *
 * @param {THREE.LineSegments} mesh
 */
export function disposeTileMesh(mesh) {
  mesh.geometry.dispose();
}
